import { ElementRef, Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { App } from "@capacitor/app";
import { firstValueFrom, Observable, of } from "rxjs";
import { EchoService, EchoSession } from "../echo-service/echo.service";
import { AppPlatformService } from "../platform/app-platform.service";
import { PerformanceModuleApiService } from "./performance-module-api.service";
import { isPerformanceMockEnabled } from "./mock/mock-config";
import { ToastService } from "../toast-service/toast.service";
import { catchError, map } from "rxjs/operators";
import { isSafeAppPath, noticeFromNotification, noticeFromUserEvent, resourceKeysOf, tierOf } from "./performance-realtime.helpers";
import { NoticeType, PerformanceNotice, ProcessStatePatch, ResourceKey } from "./performance-realtime.model";
import { PerformanceRefreshScheduler } from "./performance-refresh-scheduler";

/** Prefijo del canal privado por usuario (design.md §7.2, plan A confirmado). */
export const USER_CHANNEL = "users.";

/**
 * Prefijo del canal del proceso (design.md §4.11, C2): `echo.private()` le
 * antepone `private-`, así que el canal real es
 * `private-performance.process.{id}`, autorizado por performance-backend con
 * `performance_token` (`config/laravel-echo.ts`).
 */
export const PROCESS_CHANNEL_PREFIX = "performance.process.";

/**
 * Eventos `UserAppNotified` que llegan por `.listen()` sobre el canal privado
 * del usuario (design.md §7.2). `broadcastAs()` es dinámico y el punto
 * adelante es obligatorio. Desde C2, `ProcessStateChanged` ya no llega por
 * acá: viaja por el canal del proceso (`watchProcess()`).
 */
const USER_EVENT_TYPES: NoticeType[] = ["ProcessAssigned", "FeedbackIssued", "MeetingUpdated", "ObjectivesApprovalChanged"];

/**
 * Interruptor de emergencia (design.md §8.5): en `false`, el planificador deja
 * de ejecutar cualquier `refresh()`. El parcheo local y los avisos in-app
 * siguen funcionando igual. Es un cambio de una línea, sin tocar el resto del
 * servicio.
 */
export const REALTIME_REFETCH_ENABLED = true;

/**
 * Registro de una pantalla/servicio interesado en un `ResourceKey`
 * (design.md §7.3). `patch` intenta reflejar el aviso sin pedir nada y
 * devuelve `true` si alcanzó; `refresh` es el endpoint acotado que se llama
 * solo si el payload no alcanzó y el registro sigue visible al disparar
 * (design.md §8.2).
 */
export interface RegisteredHandle {
	host: ElementRef<HTMLElement>;
	patch?: (notice: PerformanceNotice) => boolean;
	refresh?: () => Observable<unknown>;
}

/**
 * Tiempo real de Desempeño en la app del evaluado (design.md §7). Escucha el
 * canal privado del usuario y el del workspace, despacha cada aviso por
 * `ResourceKey` a los registros de pantalla, decide si alcanza con parchear o
 * hace falta pedir (con jitter y agrupación, `PerformanceRefreshScheduler`), y
 * muestra el aviso in-app que corresponde (design.md §7.5).
 */
@Injectable({ providedIn: "root" })
export class PerformanceRealtimeService {
	private readonly registry = new Map<ResourceKey, Set<RegisteredHandle>>();
	private readonly scheduler: PerformanceRefreshScheduler;

	private session: EchoSession | null = null;
	private pendingAppPath: string | null = null;
	private hasConnectedBefore = false;

	/**
	 * Conteo de referencias de `watchProcess()` (design.md §7.3, C2): cuántas
	 * pantallas necesitan el canal de cada proceso. Sobrevive a la pérdida de
	 * sesión (§7.3: "sin tocar los conteos de las pantallas"); `processChannels`
	 * sí se vacía sin sesión y se reabre cuando `session$` vuelve a emitir.
	 */
	private readonly processChannelRefs = new Map<string, number>();
	private readonly processChannels = new Map<string, any>();

	/**
	 * Bajas de `watchProcess()` abiertas por la suscripción a TODOS los procesos
	 * activos (C4, design.md §4.12, R21), aparte de las que abre cada pantalla.
	 * Se sueltan enteras al perder la sesión y se vuelven a pedir en la
	 * siguiente (`processes/latest?active=1` cambia con el tiempo: no tiene
	 * sentido cachear la lista entre sesiones).
	 */
	private activeProcessWatches: Array<() => void> = [];
	private activeProcessesRequested = false;

	// Primer plano (design.md §8.3) = documento visible Y, en dispositivo, la última
	// `appStateChange` activa. Sin `appStateChange` (web) `appActive` se queda en `true` y
	// solo manda `visibilitychange`.
	private documentVisible = document.visibilityState !== "hidden";
	private appActive = true;

	constructor(
		private readonly echoService: EchoService,
		private readonly toastService: ToastService,
		private readonly router: Router,
		private readonly appPlatformService: AppPlatformService,
		private readonly performanceModuleApiService: PerformanceModuleApiService
	) {
		this.scheduler = new PerformanceRefreshScheduler({
			refresh: (key) => this.runRefresh(key),
			isEnabled: () => REALTIME_REFETCH_ENABLED,
		});

		this.echoService.session$.subscribe((session) => this.onSession(session));
		this.wireForegroundDetection();
	}

	/**
	 * Segundo plano (design.md §8.3): en segundo plano un evento parchea igual (no pide
	 * nada) y la clave queda pendiente, sin temporizador; al volver, se programa con la
	 * ventana más ancha entre los eventos que la ensuciaron. Se escuchan los dos porque
	 * `visibilitychange` es lo que llega en web, y en el WebView nativo `appStateChange` es
	 * la señal documentada. El doble registro en iOS es defensivo y no se probó en
	 * dispositivo (design.md §14); `applyForegroundState()` es idempotente, así que un
	 * doble disparo del mismo cambio no tiene efecto adicional.
	 */
	private wireForegroundDetection(): void {
		document.addEventListener("visibilitychange", () => this.onDocumentVisibilityChange());

		if (this.appPlatformService.isAppPlatform()) {
			App.addListener("appStateChange", ({ isActive }) => this.onAppStateChange(isActive));
		}
	}

	private onDocumentVisibilityChange(): void {
		this.documentVisible = document.visibilityState !== "hidden";
		this.applyForegroundState();
	}

	private onAppStateChange(isActive: boolean): void {
		this.appActive = isActive;
		this.applyForegroundState();
	}

	private applyForegroundState(): void {
		this.scheduler.setBackground(!(this.documentVisible && this.appActive));
	}

	/**
	 * Registra una pantalla o servicio interesado en `key` (design.md §7.3). Se
	 * llama en `ngOnInit`/`ngOnChanges` y la baja, en `ngOnDestroy`.
	 */
	register(key: ResourceKey, handle: RegisteredHandle): () => void {
		let handles = this.registry.get(key);
		if (!handles) {
			handles = new Set<RegisteredHandle>();
			this.registry.set(key, handles);
		}
		handles.add(handle);

		return () => {
			handles?.delete(handle);
			if (handles && handles.size === 0) this.registry.delete(key);
		};
	}

	/**
	 * Suscribe al canal del estado de UN proceso, con conteo de referencias
	 * (design.md §7.3, C2). El primer `watchProcess(id)` abre
	 * `private-performance.process.{id}` y registra `.ProcessStateChanged`;
	 * las siguientes llamadas solo suman una referencia. La baja resta una, y
	 * la última sale del canal (`echo.leave`). Sin sesión, la suscripción real
	 * queda pendiente hasta que `session$` emita (`onSession()`); el conteo no
	 * depende de la sesión.
	 */
	watchProcess(processId: number | string): () => void {
		const id = String(processId);
		this.processChannelRefs.set(id, (this.processChannelRefs.get(id) ?? 0) + 1);
		if (this.session) this.openProcessChannel(id);

		let released = false;
		return () => {
			if (released) return; // una baja ya ejecutada no resta una segunda vez
			released = true;

			const remaining = (this.processChannelRefs.get(id) ?? 1) - 1;
			if (remaining <= 0) {
				this.processChannelRefs.delete(id);
				this.closeProcessChannel(id);
			} else {
				this.processChannelRefs.set(id, remaining);
			}
		};
	}

	private openProcessChannel(id: string): void {
		if (!this.session || this.processChannels.has(id)) return;

		const channel = this.session.echo.private(PROCESS_CHANNEL_PREFIX + id);
		channel.listen(".ProcessStateChanged", (payload: Record<string, unknown>) => {
			this.dispatch(noticeFromUserEvent("ProcessStateChanged", payload));
		});
		this.processChannels.set(id, channel);
	}

	private closeProcessChannel(id: string): void {
		if (!this.processChannels.has(id)) return;
		this.processChannels.delete(id);
		this.session?.echo.leave(PROCESS_CHANNEL_PREFIX + id);
	}

	/**
	 * true si el socket está conectado ahora mismo. Lo usa `PushNotificationsService`
	 * para no duplicar un aviso que el socket ya pintó como toast (design.md §7.6).
	 */
	isSocketConnected(): boolean {
		return this.session?.echo?.connector?.pusher?.connection?.state === "connected";
	}

	/**
	 * Navega a `path` si es una ruta segura de Desempeño (`isSafeAppPath`). Sin
	 * sesión todavía (arranque en frío desde un push), guarda la ruta y navega
	 * apenas `session$` emite una sesión (design.md §7.6).
	 */
	openAppPath(path: string | null | undefined): void {
		if (!isSafeAppPath(path)) return;

		if (!this.session) {
			this.pendingAppPath = path as string;
			return;
		}

		void this.router.navigateByUrl(path as string);
	}

	private onSession(session: EchoSession | null): void {
		this.session = session;

		if (!session) {
			// Sin sesión los canales reales ya no existen, pero los conteos de
			// `watchProcess()` quedan intactos: se reabren solos al volver (§7.3).
			this.processChannels.clear();
			this.releaseActiveProcessWatches();
			return;
		}

		this.subscribeToChannels(session);

		// Reabre los canales de proceso que alguna pantalla sigue necesitando.
		for (const id of this.processChannelRefs.keys()) this.openProcessChannel(id);

		this.watchActiveProcesses();

		if (this.pendingAppPath) {
			const path = this.pendingAppPath;
			this.pendingAppPath = null;
			void this.router.navigateByUrl(path);
		}
	}

	/**
	 * R21 (design.md §4.12): al entrar a `/performance` la app se suscribe al
	 * canal de TODOS los procesos activos de la persona, no solo a los que una
	 * pantalla muestra — así el toast de un `notice` llega aunque nadie tenga
	 * abierta ni el inicio ni la vista de ese proceso. Usa `watchProcess()`, así
	 * que comparte canal y conteo con cualquier pantalla que también lo pida
	 * (una pantalla del mismo proceso MUST NOT abrir otro canal, R21).
	 *
	 * Solo con el `performance_token` ya obtenido: `session$` emite en CUALQUIER
	 * inicio de sesión (también en aprendizaje), y sin ese token el pedido sale
	 * sin `Authorization`, el backend de Desempeño responde 401 y el interceptor
	 * cierra la sesión recién abierta (así se caía el «Acceder como»). Por eso
	 * `AppComponent` lo vuelve a llamar al entrar a `/performance`, cuando el guard
	 * ya consiguió el token. Idempotente dentro de una misma sesión.
	 */
	watchActiveProcesses(): void {
		if (!this.session || this.activeProcessesRequested) return;
		if (!isPerformanceMockEnabled() && !localStorage.getItem("performance_token")) return;

		this.activeProcessesRequested = true;
		this.performanceModuleApiService
			.getLatestProcesses(50, { active: true })
			.pipe(
				map((res) => res?.data ?? []),
				catchError(() => of([])),
			)
			.subscribe((processes) => {
				for (const process of processes) {
					this.activeProcessWatches.push(this.watchProcess(process.id));
				}
			});
	}

	private releaseActiveProcessWatches(): void {
		for (const unwatch of this.activeProcessWatches) unwatch();
		this.activeProcessWatches = [];
		this.activeProcessesRequested = false;
	}

	private subscribeToChannels(session: EchoSession): void {
		const userChannel = session.echo.private(USER_CHANNEL + session.userId);

		userChannel.notification((notification: Record<string, unknown>) => {
			this.dispatch(noticeFromNotification(notification));
		});

		for (const eventType of USER_EVENT_TYPES) {
			userChannel.listen("." + eventType, (payload: Record<string, unknown>) => {
				this.dispatch(noticeFromUserEvent(eventType, payload));
			});
		}

		if (session.workspaceSlug) {
			const workspaceChannel = session.echo.channel("app." + session.workspaceSlug);
			workspaceChannel.listen(".WorkspaceAppRefresh", (payload: Record<string, unknown>) => {
				this.dispatch(noticeFromUserEvent("WorkspaceAppRefresh", payload));
			});
		}

		const connection = session.echo?.connector?.pusher?.connection;
		if (connection?.bind) {
			connection.bind("state_change", ({ current }: { previous: string; current: string }) => {
				if (current !== "connected") return;

				// La primera conexión de la sesión no es una reconexión (design.md §8.4).
				if (this.hasConnectedBefore) {
					this.scheduler.reconnect(this.visibleKeys());
				}
				this.hasConnectedBefore = true;
			});
		}
	}

	/**
	 * Despacho: evento → recurso → parche o recarga acotada → aviso (design.md
	 * §7.2, §7.5). Desde C4 (design.md §4.12, ADR-11), `process.stage.launched`/
	 * `.reopened`/`process.closed` ya no llegan por acá como aviso de nivel
	 * superior (no tienen entrada en `RESOURCE_KEYS_BY_TYPE`, `resourceKeysOf()`
	 * les da `[]`): viajan dentro de `ProcessStateChanged.notices`, y
	 * `notifyStageNotices()` es quien los muestra. `ProcessAssigned` además suma
	 * el canal del proceso nuevo (R21): la app recién se entera de que existe.
	 */
	private dispatch(notice: PerformanceNotice): void {
		if (notice.type === "ProcessAssigned" && notice.process_id != null) {
			this.activeProcessWatches.push(this.watchProcess(notice.process_id));
		}

		const keys = resourceKeysOf(notice);
		const tier = tierOf(notice);
		let patched = false;

		for (const key of keys) {
			const handles = this.registry.get(key);
			if (!handles) continue;

			for (const handle of handles) {
				if (handle.patch?.(notice)) patched = true;
			}
		}

		if (!patched) {
			for (const key of keys) {
				if (this.registry.has(key)) this.scheduler.notify(key, tier);
			}
		}

		const hasVisibleTarget = keys.some((key) => this.hasVisibleHandle(key));

		if (notice.type === "ProcessStateChanged") {
			// El estado en sí nunca es un aviso (R16): los toasts, si hay, salen de
			// `notices` (C4, design.md §4.12), uno por elemento, en orden (R22).
			this.notifyStageNotices(notice, hasVisibleTarget);
			return;
		}

		this.notify(notice, hasVisibleTarget);
	}

	/**
	 * Regla de avisos in-app (design.md §7.5): `ObjectivesApprovalChanged` no
	 * lleva toast propio (el aviso con el conteo llega aparte). Sin ninguna
	 * pantalla afectada visible, el aviso lleva la acción «Ver»; si la persona
	 * ya está en esa pantalla, el aviso es breve y sin acción. Solo dentro de
	 * `/performance`: fuera del módulo, el push ya cubre el caso.
	 */
	private notify(notice: PerformanceNotice, hasVisibleTarget: boolean): void {
		if (notice.type === "ObjectivesApprovalChanged") return;
		if (!this.router.url.startsWith("/performance")) return;

		this.presentToastFor(this.titleFor(notice), notice.app_path, hasVisibleTarget);
	}

	/**
	 * Un toast por cada `notice` de `ProcessStateChanged.notices` (C4, design.md
	 * §4.12, ADR-11, R22): los avisos de etapa/cierre ya no llegan por
	 * `.notification()` (R20) — vienen aquí, dentro del mismo evento de estado.
	 * Sin `notices` (casilla apagada) no hay nada que mostrar. Sin filtro de
	 * autor ni de rol: son para todos los participantes activos.
	 */
	private notifyStageNotices(envelope: PerformanceNotice, hasVisibleTarget: boolean): void {
		const notices = (envelope.patch as ProcessStatePatch | undefined)?.notices;
		if (!notices?.length) return;
		if (!this.router.url.startsWith("/performance")) return;

		for (const item of notices) {
			this.presentToastFor(item.title, item.app_path, hasVisibleTarget);
		}
	}

	/** Breve si la pantalla afectada está visible; con «Ver» si no (R11). */
	private presentToastFor(title: string, appPath: string | null | undefined, hasVisibleTarget: boolean): void {
		if (hasVisibleTarget || !isSafeAppPath(appPath)) {
			void this.toastService.presentToast("informative", title, 3000, "top");
			return;
		}

		void this.toastService.presentActionToast("informative", title, 6000, "top", {
			text: "Ver",
			handler: () => this.openAppPath(appPath),
		});
	}

	/**
	 * El aviso de una sesión live lleva su título. El de un proceso dice qué pasó
	 * («Se reabrió la etapa de Seguimiento») y de qué proceso; si no trae título,
	 * queda solo el nombre del proceso, que siempre viene.
	 */
	private titleFor(notice: PerformanceNotice): string {
		if (notice.type === "MeetingUpdated") return notice.title ?? "";
		return [notice.title, notice.process_name].filter(Boolean).join(" · ");
	}

	private runRefresh(key: ResourceKey): Promise<unknown> {
		const handles = this.registry.get(key);
		if (!handles) return Promise.resolve();

		for (const handle of handles) {
			if (this.isVisible(handle.host) && handle.refresh) {
				return firstValueFrom(handle.refresh());
			}
		}

		// Ya nadie visible para esta clave: se descarta (design.md §8.2). La pantalla
		// que se vuelva a abrir pide en su propio `ionViewWillEnter`.
		return Promise.resolve();
	}

	private hasVisibleHandle(key: ResourceKey): boolean {
		const handles = this.registry.get(key);
		if (!handles) return false;

		for (const handle of handles) {
			if (this.isVisible(handle.host)) return true;
		}
		return false;
	}

	private visibleKeys(): ResourceKey[] {
		const keys: ResourceKey[] = [];
		for (const key of this.registry.keys()) {
			if (this.hasVisibleHandle(key)) keys.push(key);
		}
		return keys;
	}

	/** Visible = el `host` no está dentro de un `.ion-page-hidden` (design.md §7.3). */
	private isVisible(host: ElementRef<HTMLElement> | undefined): boolean {
		const element = host?.nativeElement;
		if (!element) return false;
		return element.closest(".ion-page-hidden") === null;
	}
}
